import './TimelineRecord.scss';
import { convertISODateToShortDate } from '@sonar-software/sonar-ts-lib';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { getChangeTypeToChangeLabel } from '@timeline/@arch/lib';
import { SonarRecord } from '@ui/records';
import { TimelineEntry } from '@sonar-software/sonar-core';
import { CodeComparison, ExpandCarat } from '@ui/general';
import { FileAddOutlined, RobotOutlined } from '@ant-design/icons';
import { off, on } from '@lib/events';
import React, { useCallback, useState } from 'react';
import { useOnMount, useOnUnmount } from '@hooks';

export interface TimelineRecordProps {
	parentSonarObjectId: number;
	record: TimelineEntry;
}

export const TimelineRecord: React.FC<TimelineRecordProps> = ({ parentSonarObjectId, record }) => {
	const [ isExpanded, setIsExpanded ] = useState(true);

	const isExpandable = Boolean(record.change.changeDetail);

	const handleClick = useCallback(() => setIsExpanded((value) => !value), []);
	const handleExpand = useCallback(() => setIsExpanded(true), []);
	const handleCollapse = useCallback(() => setIsExpanded(false), []);

	useOnMount(() => {
		if (!isExpandable) {
			return;
		}

		on(`periscope-timeline-${parentSonarObjectId}:expandAll`, handleExpand);
		on(`periscope-timeline-${parentSonarObjectId}:collapseAll`, handleCollapse);
	});

	useOnUnmount(() => {
		if (!isExpandable) {
			return;
		}

		off(`periscope-timeline-${parentSonarObjectId}:expandAll`, handleExpand);
		off(`periscope-timeline-${parentSonarObjectId}:collapseAll`, handleCollapse);
	});

	const labelBase = getChangeTypeToChangeLabel(record.change.changeType);
	const label = isExpandable ? `${labelBase} - ${record.change.changeDetail}` : labelBase;

	return (
		<div className='timeline-record'>
			<SonarRecord
				name={
					<>
						{ isExpandable ?
							<RobotOutlined className='timeline-record-icon' />						 :
							<FileAddOutlined className='timeline-record-icon' /> }
						<span className='timeline-record-label'>{ label }</span>
						{ isExpandable &&
							<ExpandCarat
								className='timeline-record-expand'
								expanded={ isExpanded }
								onClick={ handleClick } /> }
					</>
				}
				onClick={ isExpandable ? handleClick : undefined }
				secondaryInfo={
					<>
						<span>{ record.lastModifiedByName }</span>
						<span className='timeline-record-timestamp'>
							{ convertISODateToShortDate(record.change.timestamp) }
						</span>
					</>
				}
				sonarObjectId={ record.sonarObjectId }>
				{ isExpandable && isExpanded &&
					<div className='timeline-record-diff-row'>
						<CodeComparison
							newCode={ record.change.newValue ?? COPY_SPECIFIC.NONE_PARENTHETICAL }
							oldCode={ record.change.oldValue ?? COPY_SPECIFIC.NONE_PARENTHETICAL } />
					</div> }
			</SonarRecord>
		</div>
	);
};
